import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService, HttpMethod } from '../../../../shared/services/api/api.service';
import { ToasterService } from '../../../../shared/services/toaster/toaster.service';
import { AiBudgetService } from '../../../../shared/services/ai-budget/ai-budget.service';
import { AiBudgetSnapshot } from '../../../../shared/services/ai-budget/ai-budget.types';

interface OrganizationRow {
  ORGANIZATION_ID?: number;
  ORGANIZATION_NAME: string;
}

interface OrganizationsListResponse {
  data: OrganizationRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface OrgBudgetRow {
  orgId: number;
  orgName: string;
  snapshot: AiBudgetSnapshot | null;
}

@Component({
  selector: 'app-ai-usage-admin',
  template: `
    <div class="ai-usage-admin">
      <div class="toolbar">
        <button type="button" (click)="loadBudgets()" [disabled]="isLoadingOrgs">
          Refresh
        </button>
      </div>

      <p *ngIf="isLoadingOrgs">Loading…</p>

      <p *ngIf="!isLoadingOrgs && orgBudgets.length === 0">No organizations loaded.</p>

      <table *ngIf="!isLoadingOrgs && orgBudgets.length > 0" class="data-table">
        <thead>
          <tr>
            <th>Org name</th>
            <th>Cap (USD)</th>
            <th>Used (USD)</th>
            <th>Remaining (USD)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of orgBudgets; trackBy: trackByOrgId">
            <td>{{ row.orgName }}</td>
            <td>{{ row.snapshot != null ? (row.snapshot.capUsd | currency:'USD') : '—' }}</td>
            <td>{{ row.snapshot != null ? (row.snapshot.usedUsd | currency:'USD') : '—' }}</td>
            <td>
              {{ row.snapshot != null ? (row.snapshot.remainingUsd | currency:'USD') : '—' }}
            </td>
            <td>
              <saffron-button text="Edit" (click)="onEditBudgetClick(row.orgId)"></saffron-button>
            </td>
          </tr>
        </tbody>
      </table>

      <app-edit-org-budget-modal
        *ngIf="selectedBudgetOrgId !== null"
        [organizationId]="selectedBudgetOrgId"
        [currentSnapshot]="selectedBudgetSnapshot"
        [open]="editModalOpen"
        (closed)="onBudgetModalClosed()"
        (saved)="onBudgetSaved($event)"
      ></app-edit-org-budget-modal>
    </div>
  `,
  styles: [
    `
      :host { display: block; }
      .ai-usage-admin { padding: 0; }
      .toolbar {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }
      .data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.875rem;
      }
      .data-table th,
      .data-table td {
        border: 1px solid #e5e5e5;
        padding: 0.5rem 0.75rem;
        text-align: left;
        vertical-align: middle;
      }
      .data-table th {
        background: #fafaf9;
        font-weight: 600;
        color: #6b6b6b;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 0.75rem;
      }
    `,
  ],
})
export class AiUsageAdminComponent implements OnInit, OnDestroy {
  orgBudgets: OrgBudgetRow[] = [];
  isLoadingOrgs = false;

  selectedBudgetOrgId: number | null = null;
  editModalOpen = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly apiService: ApiService,
    private readonly aiBudgetService: AiBudgetService,
    private readonly toaster: ToasterService,
  ) {}

  ngOnInit(): void {
    void this.loadBudgets();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadBudgets(): Promise<void> {
    this.isLoadingOrgs = true;
    try {
      const orgs = await this.fetchAllOrganizations();
      this.orgBudgets = orgs
        .filter((o) => o.ORGANIZATION_ID != null)
        .map((o) => ({
          orgId: o.ORGANIZATION_ID as number,
          orgName: o.ORGANIZATION_NAME,
          snapshot: null,
        }));
      for (const row of this.orgBudgets) {
        this.aiBudgetService
          .getOrgBudget(row.orgId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (snap) => {
              row.snapshot = snap;
            },
            error: () => {
              // Leave snapshot null when the org has no budget configured.
            },
          });
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Failed to load AI usage: ${msg}`, 5000);
      console.error(error);
    } finally {
      this.isLoadingOrgs = false;
    }
  }

  onEditBudgetClick(orgId: number): void {
    this.selectedBudgetOrgId = orgId;
    this.editModalOpen = true;
  }

  onBudgetModalClosed(): void {
    this.editModalOpen = false;
    this.selectedBudgetOrgId = null;
  }

  onBudgetSaved(snapshot: AiBudgetSnapshot): void {
    const row = this.orgBudgets.find((r) => r.orgId === this.selectedBudgetOrgId);
    if (row) {
      row.snapshot = snapshot;
    }
  }

  get selectedBudgetSnapshot(): AiBudgetSnapshot | null {
    if (this.selectedBudgetOrgId == null) {
      return null;
    }
    return (
      this.orgBudgets.find((r) => r.orgId === this.selectedBudgetOrgId)?.snapshot ?? null
    );
  }

  trackByOrgId(_: number, row: OrgBudgetRow): number {
    return row.orgId;
  }

  // `GET /organizations` enforces pageSize <= 100, so page through until we
  // either run out of rows or hit the hard cap.
  private async fetchAllOrganizations(): Promise<OrganizationRow[]> {
    const pageSize = 100;
    const maxPages = 50;
    const collected: OrganizationRow[] = [];
    for (let page = 1; page <= maxPages; page += 1) {
      const response = await this.apiService.call<OrganizationsListResponse>(
        HttpMethod.GET,
        `organizations?page=${page}&pageSize=${pageSize}`,
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      collected.push(...rows);
      const total = typeof response?.total === 'number' ? response.total : collected.length;
      if (rows.length < pageSize || collected.length >= total) {
        break;
      }
    }
    return collected;
  }
}
